import { transactionConfirmedPayment, transactionPaidByBank } from "../../../../shared/transactionPayments";
import React, { useEffect, useState, useCallback, useContext } from 'react';
import { SimpleGrid, Loader, Text, Group, Grid, ActionIcon, ThemeIcon, Image, Space, Stack, Box, Paper, Title, NumberFormatter, Table } from '@mantine/core';
import { Link, } from 'react-router-dom';
import { IconMoneybag, IconBuildingBank, IconPhone, IconFile, IconRefresh, IconSchool, IconLock } from '@tabler/icons-react';
import { DocumentReference, doc, onSnapshot, DocumentData } from 'firebase/firestore';
import { CustomInfoCard } from '../../routes/customers/CustomInfoCard';
import { Customer, Portfolio, KfhTradeAccount, Account, GenevaRequest, CustomClaims, PortfolioPositionWithCustodianAccountsResponse, Security, FirebaseTransaction } from '../../types';
import TransactionsActions from './transactionsActions';
import { httpsCallable } from 'firebase/functions';
import { useFunctions } from '../../helpers/firebaseContext';
import { MyUserDataContext } from "../../contexts";
import CustomProductComment from '../../components/custom_product_comment';
import TransactionPaymentCard from "./transaction_payment_card";
import TransactionSecurityKcc from "./transaction_security_kcc";


interface TransactionInfoProps {
    transactionObj: FirebaseTransaction;
    transactionRef: DocumentReference,
    customerRef?: DocumentReference;
    customerFullData: Customer | null;
    kycData: DocumentData | null;
    kycState: DocumentData | undefined;
}

// Reusable Refresh Button Component
const RefreshButton: React.FC<{ onClick: () => void; isLoading?: boolean }> = ({ onClick, isLoading = false }) => (
    <ActionIcon
        variant="subtle"
        onClick={onClick}
        loading={isLoading}
    >
        <ThemeIcon color="blue" variant="light">
            <IconRefresh size={16} />
        </ThemeIcon>
    </ActionIcon>
);


const TransactionInfo: React.FC<TransactionInfoProps> = ({
    transactionObj,
    customerRef,
    customerFullData,
    kycState,
    transactionRef
}) => {

    const [bankAccount, setBankAccount] = useState<Account | null>(null);
    const [bankAccountLoading, setBankAccountLoading] = useState<boolean>(false);
    const functions = useFunctions();
    functions.region = "europe-west1";

    const appContext = useContext(MyUserDataContext);

    let userClaims = appContext.firebaseTokenResult?.claims.customClaims as CustomClaims;
    // Extracting portfolio information from transaction object
    let currentPortfolio = transactionObj?.product as Portfolio;
    let currentKfhTradeAccount = transactionObj?.product as KfhTradeAccount;

    const [isPositionLoading, setIsPositionLoading] = useState(false);
    const [portfolioPosition, setPortfolioPosition] = useState<number | null>(null);
    const [errorPosition, setPositionError] = useState<string>('');

    // Fetch bank account data based on iban from transaction
    useEffect(() => {
        if (!customerRef || !transactionObj?.iban) {
            setBankAccount(null);
            setBankAccountLoading(false);
            return;
        }

        setBankAccountLoading(true);
        /// Now iban is mandatory
        const accountDocRef = doc(customerRef, 'accounts', transactionObj.iban);

        const unsubscribe = onSnapshot(accountDocRef, (snapshot) => {
            if (snapshot.exists()) {
                const accountData = snapshot.data() as Account;
                setBankAccount(accountData);
            } else {
                setBankAccount(null);
            }
            setBankAccountLoading(false);
        }, (error) => {
            console.error("Error loading bank account:", error);
            setBankAccountLoading(false);
        });

        return () => unsubscribe();
    }, [customerRef, transactionObj?.iban]);

    const fetchPortfolioPosition = useCallback(async () => {
        if (!transactionObj || !transactionObj.id) {
            return;
        }

        if (transactionObj.source != "portfolio") {
            return
        }
        try {
            console.log('[PortfolioPosition] => Starting fetch for transaction:', transactionObj.id);

            // Set loading state
            setIsPositionLoading(true);
            setPositionError('');

            // Create the callable function
            const getPortfolioPosition = httpsCallable<GenevaRequest, PortfolioPositionWithCustodianAccountsResponse>(
                functions,
                'getPortfolioPositionWithCustodianAccounts'
            );

            // Build request object
            let requestObject: any = {
                portfolioCode: (transactionObj.product as Portfolio)!.code,
            };

            if (transactionObj.custom_date) {
                requestObject["date"] = transactionObj.transactionTimestamp;
            }

            console.log('[PortfolioPosition] => Request object:', JSON.stringify(requestObject, null, 2));

            // Call the function
            const result = await getPortfolioPosition(requestObject);
            console.log('[PortfolioPosition] => API result:', result);

            // Find matching custodian account
            // const rows = (result.data as { rows?: PortfolioPositionWithCustodianAccountsRow[] })?.rows || [];
            const rows = result.data.rows;

            console.log('[PortfolioPosition] => Looking for custodian account:', transactionObj.custodianAccount?.NAMELINE1);

            const matchingRows = rows.filter((row) => row.CUSTODIANACCOUNT === transactionObj.custodianAccount?.NAMELINE1);

            if (matchingRows.length > 0) {
                console.log('[PortfolioPosition] => Found matching rows:', matchingRows.length);
                // Sum up all QTYEND values
                const totalQtyEnd = matchingRows.reduce((sum, row) => sum + (row.QTYEND || 0), 0);
                console.log('[PortfolioPosition] => Total QTYEND:', totalQtyEnd);
                setPortfolioPosition(totalQtyEnd);
            }

        } catch (error) {
            console.error('[PortfolioPosition] => Error:', error);
            setPositionError('Failed to fetch portfolio position');
        } finally {
            setIsPositionLoading(false);
        }
    }, [transactionObj, functions]);

    // Portfolio Position Value Component
    const PortfolioPositionValue = () => {
        if (isPositionLoading) {
            return (
                <Group gap="xs">
                    <Loader size="sm" color="yellow" />
                    <Text c="yellow" size="sm">Fetching position...</Text>
                </Group>
            );
        }

        if (errorPosition) {
            return (
                <Group gap="xs">
                    <Text c="red">{errorPosition}</Text>
                    <RefreshButton onClick={fetchPortfolioPosition} />
                </Group>
            );
        }

        if (portfolioPosition !== null && portfolioPosition !== undefined) {
            return (
                <Group gap="xs">
                    <Text c={portfolioPosition >= 0 ? "green" : "red"}>
                        {portfolioPosition.toFixed(3)}
                    </Text>
                    <RefreshButton onClick={fetchPortfolioPosition} />
                </Group>
            );
        }

        return (
            <Group gap="xs">
                <Text c="gray">No data</Text>
                <RefreshButton onClick={fetchPortfolioPosition} />
            </Group>
        );
    };

    // Fetch Portfolio Position With CustodianAccounts based on transaction CustodianAccounts object
    useEffect(() => {
        fetchPortfolioPosition();
    }, [fetchPortfolioPosition]);

    //can also convert arrays by recursively calling this function.  Can break in amazing fashion if array contents are not strings.
    const convertCamelCase = (str: any): string => {

        if (typeof str === "string") {
            return str.replace(/([A-Z]+)/g, " $1").replace(/([A-Z][a-z])/g, " $1");
        } else if (Array.isArray(str)) {
            return str.map((item: any) => convertCamelCase(item))?.join(", ");
        }
        return str;
    };


    // Helper function to format date
    const formatDate = (timestamp: any) => {
        if (!timestamp) return 'N/A';

        let date;
        if (timestamp?.toDate) {
            date = timestamp.toDate();
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else {
            date = new Date(timestamp);
        }

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Helper function to format currency
    const formatCurrency = (amount: number, currency: string = 'KWD') => {
        if (amount === undefined || amount === null) return 'N/A';
        return `${amount.toLocaleString()} ${currency}`;
    };

    return (
        <>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
                {/* Transaction Details Card */}
                <CustomInfoCard
                    title="Transaction Details"
                    color='blue'
                    icon={<IconMoneybag size={16} />}
                    list={[
                        {
                            label: "Transaction ID",
                            value: transactionObj?.id || 'N/A',
                        },
                        {
                            label: "Source ",
                            value: transactionObj?.source,
                        },
                        {
                            label: "Type",
                            value: convertCamelCase(transactionObj?.transactionType || 'N/A').replace('_', ' ')
                        },
                        {

                            label: "Amount",
                            value: transactionObj.source === "portfolio" ? formatCurrency(transactionObj?.amount || 0, transactionObj?.currency) : undefined,
                        },
                        ...(transactionObj.paymentSlipRequired ? [{
                            label: "Securities transfer fee",
                            value: `${(transactionObj.securityTransferFee ?? 0).toFixed(3)} KWD (${transactionObj.securityTransferSecurityCount ?? 0} securities × 6.250 KWD)`,
                        }, {
                            label: "Paid through KFH Online",
                            value: `${transactionConfirmedPayment(transactionObj).toFixed(3)} KWD`,
                        }] : []),
                        {
                            label: "Converted Amount",
                            value: transactionObj.source === "portfolio" ? formatCurrency(transactionObj?.convertedAmount || 0, transactionObj?.currencyToValue) : undefined,
                        },
                        ...(transactionObj?.currency ? [{
                            label: "Currency",
                            value: transactionObj?.currency || 'N/A',
                        },] : []
                        ),
                        // show All Available Cash data if available
                        ...((transactionObj?.is_all_available_cash != null && transactionObj?.source === "portfolio") ? [
                            {
                                label: "All Available Cash",
                                value: transactionObj?.is_all_available_cash,
                            }
                        ] : []),
                        {
                            label: "Create Date",
                            value: formatDate(transactionObj?.created),
                        },
                        // show Geneva Status data if available
                        ...(transactionObj?.geneva_status ? [
                            {
                                label: "Geneva Status",
                                value: convertCamelCase(transactionObj.geneva_status).replace('_', ' '),
                            }
                        ] : []),
                        // show Geneva Reason data if available
                        ...(transactionObj?.geneva_reason ? [
                            {
                                label: "Geneva Reason",
                                value: transactionObj?.geneva_reason,
                            }
                        ] : []),
                        {
                            label: "KYC Status",
                            value:
                                <Link to={`/customers/${customerRef?.id}`}>
                                    {<Text c="yellow">{kycState?.status || "No KYC"}</Text>}
                                </Link>
                        },
                        {
                            label: "KYC ID",
                            value: transactionObj?.kyc_id || "No KYC Linked",
                            noCapitalize: true,
                        },
                    ]}
                />

                {/* Current Portfolio Information Card */}
                {transactionObj?.source === "portfolio" && currentPortfolio ? (
                    <CustomInfoCard
                        title={`Portfolio Information`}
                        color='blue'
                        icon={<IconFile size={16} />}
                        list={[
                            {
                                label: "Portfolio ID",
                                value: currentPortfolio.id || 'N/A',
                            },
                            {
                                label: "Code",
                                value: currentPortfolio.code || 'N/A',
                            },
                            {
                                label: "PF Number",
                                value: currentPortfolio.name || 'N/A',
                            },
                            {
                                label: "Investment Portfolio",
                                value: currentPortfolio.investmentPortfolio ?? 'NA',
                            },

                            // Add position data if available
                            ...(currentPortfolio.position ? [

                                {
                                    label: "PF Status",
                                    value: currentPortfolio.position?.status ?? 'NA',
                                },
                                {
                                    label: "Opening Date",
                                    value: formatDate(currentPortfolio.position?.created),
                                },
                                {
                                    label: "Cash",
                                    value: currentPortfolio.position.cash ?? 'NA',
                                },
                                {
                                    label: "PF Type",
                                    value: currentPortfolio.position.type ?? 'NA',
                                },
                                {
                                    label: "Managed By",
                                    value: currentPortfolio.position.managed_by ?? 'NA',
                                },
                                {
                                    label: "Cash Dividend YTD",
                                    value: currentPortfolio.position.cash_dividend_ytd ? formatCurrency(currentPortfolio.position.cash_dividend_ytd) : 'N/A',
                                }
                            ] : []),
                            // Add risk tolerance data if available
                            ...(currentPortfolio.riskTolerance ? [
                                {
                                    label: "Risk Tolerance",
                                    value: JSON.stringify(currentPortfolio.riskTolerance),
                                }
                            ] : []),
                            ...(userClaims["admin"] ? [
                                {
                                    label: "Cash In Position",
                                    value: <PortfolioPositionValue />
                                }
                            ] : []),
                        ]}
                    />
                ) : (transactionObj?.source === "portfolio" && transactionObj?.product?.id) ? (
                    <CustomInfoCard
                        title="Portfolio Information"
                        color="red"
                        icon={<IconFile size={16} />}
                        list={[{ label: "Error", value: "Portfolio not found" }]}
                    />
                ) : null}

                {/* Current KFH Trade Account Information Card */}
                {transactionObj?.source === "kfhtrade" && currentKfhTradeAccount ? (
                    <CustomInfoCard
                        title="KFH Trade Account Information"
                        color='blue'
                        icon={<IconFile size={16} />}
                        list={[
                            {
                                label: "KFH Trade Account ID",
                                value: currentKfhTradeAccount.id || 'N/A',
                            },
                            {
                                label: "KFH Trade ID",
                                value: currentKfhTradeAccount.KFHTRADE_ID || 'N/A',
                            },
                            {
                                label: "Account Name",
                                value: currentKfhTradeAccount.ACCOUNT_NAME || 'N/A',
                            },
                            {
                                label: "CRM ID",
                                value: currentKfhTradeAccount.CRM_ID || 'N/A',
                            },
                            {
                                label: "Currency",
                                value: currentKfhTradeAccount.CURRENCY || 'N/A',
                            },
                            // {
                            //     label: "Investor Account No",
                            //     value: currentKfhTradeAccount.INVESTOR_ACC_NO || 'N/A',
                            // },
                            {
                                label: "Source",
                                value: currentKfhTradeAccount.SOURCE || 'N/A',
                            },
                            {
                                label: "Created Date",
                                value: currentKfhTradeAccount.CREATED_DATE ? formatDate(currentKfhTradeAccount.CREATED_DATE) : 'N/A',
                            },
                            // Add cash balance data if available
                            ...((["cash_in", "cash_out"].includes(transactionObj?.transactionType) && currentKfhTradeAccount.cash_balance?.balance || currentKfhTradeAccount.cash_balance?.blockedAmount) ? [
                                {
                                    label: "Cash Balance",
                                    value: formatCurrency(currentKfhTradeAccount.cash_balance.balance ?? 0, currentKfhTradeAccount.CURRENCY),
                                },
                                {
                                    label: "Blocked Amount",
                                    value: formatCurrency(currentKfhTradeAccount.cash_balance.blockedAmount ?? 0, currentKfhTradeAccount.CURRENCY),
                                },
                            ] : []),
                        ]}
                    />
                ) : (transactionObj?.source === "kfhtrade" && transactionObj?.product?.id) ? (
                    <CustomInfoCard
                        title="KFH Trade Account Information"
                        color="red"
                        icon={<IconFile size={16} />}
                        list={[{ label: "Error", value: "KFH Trade account not found" }]}
                    />
                ) : null}




                {/* Client Information Card */}
                <CustomInfoCard
                    title="Client Information"
                    color="yellow"
                    icon={<IconPhone size={16} />}
                    list={[
                        {
                            label: "Name (English)",
                            value: customerFullData?.name_en || 'N/A',
                        },
                        {
                            label: "Name (Arabic)",
                            value: customerFullData?.name_ar || 'N/A',
                        },
                        {
                            label: "Civil ID",
                            value: customerFullData?.civilID || 'N/A',
                        },
                        {
                            label: "Mobile",
                            value: customerFullData?.mobile || 'N/A',
                        },
                        {
                            label: "Nationality",
                            value: customerFullData?.nationality || 'N/A',
                        },
                        {
                            label: "Gender",
                            value: customerFullData?.gender || 'N/A',
                        },
                    ]}
                />


                {/* Security Transaction  Information Card */}
                {["security_out", "security_in"].includes(transactionObj?.transactionType) ?
                    <Paper shadow="xs" p="md" >
                        <Group justify="space-between" wrap="nowrap" align="flex-start">
                            <Group wrap="nowrap" style={{ flex: 1 }}>
                                <ThemeIcon color={"blue"} variant="light">
                                    <IconLock size={16} />
                                </ThemeIcon>
                                <Title order={5}>
                                </Title>
                                {"Securities"}
                            </Group>

                        </Group>

                        <Table variant="vertical" layout="fixed" withTableBorder mt={"md"}>
                            <Table.Thead>
                                <Table.Tr>
                                    <Table.Th>Security</Table.Th>
                                    {transactionObj.source === "kfhtrade" && <Table.Th>KCC Number</Table.Th>}
                                    {transactionObj?.transactionType === "security_in" && <Table.Th>Price</Table.Th>}
                                    <Table.Th>Quantity</Table.Th>
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {transactionObj?.securities?.map((securityObj: { price?: number, quantity: number, value?: Security }, index: number) => (
                                    <Table.Tr key={securityObj?.value?.CODE ?? index}>
                                        <Table.Td bg="white" pr="sm">

                                            <Text size="sm" fw={500}>
                                                {`${securityObj?.value?.CODE} (${securityObj?.value?.EXCHANGE_CODE})`}
                                            </Text>
                                        </Table.Td>
                                        {transactionObj.source === "kfhtrade" && <Table.Td bg="white" pr="sm">
                                            <TransactionSecurityKcc security={securityObj.value} />
                                        </Table.Td>}
                                        {transactionObj?.transactionType === "security_in" && <Table.Td bg="white" pr="sm">

                                            {securityObj.price && (
                                                <Group >
                                                    <Text size="xs" c="dimmed">Price </Text>
                                                    <Text size="sm" ta="right">
                                                        <NumberFormatter thousandSeparator value={securityObj.price} />
                                                    </Text>
                                                </Group>
                                            )}
                                        </Table.Td>}
                                        <Table.Td bg="white" pr="sm">
                                            {securityObj.quantity != null && (
                                                <Group justify="space-between">
                                                    <Text size="xs" c="dimmed">Quantity</Text>
                                                    <Text size="sm" ta="right">
                                                        <NumberFormatter thousandSeparator value={securityObj.quantity} />
                                                    </Text>
                                                </Group>
                                            )}
                                        </Table.Td>

                                    </Table.Tr>


                                ))}
                            </Table.Tbody>
                        </Table>

                    </Paper>
                    : <></>
                }


                {/* Bank Account Information Card */}
                {bankAccountLoading ? (
                    <CustomInfoCard
                        title="Bank Account Details"
                        color='green'
                        icon={<IconBuildingBank size={16} />}
                        list={[{ label: "Loading...", value: <Loader size="sm" /> }]}
                    />
                ) : bankAccount ? (
                    <CustomInfoCard
                        title="Bank Account Information"
                        color="red"
                        attention={bankAccount?.verified === false}
                        icon={
                            <IconBuildingBank size={16} />
                        }
                        list={[

                            {
                                label: 'IBAN',
                                value: bankAccount.iban,
                                noCapitalize: true,

                            },
                            {
                                label: 'Account',
                                value: bankAccount.account,
                                noCapitalize: true,
                            },
                            {
                                label: 'Verified',
                                value: bankAccount?.verified,
                                noCapitalize: true,
                            },

                        ]}
                    />

                ) : transactionObj?.accountId ? (
                    <CustomInfoCard
                        title="Bank Account Details"
                        color="red"
                        icon={<IconBuildingBank size={16} />}
                        list={[{ label: "Error", value: "Bank account not found" }]}
                    />
                ) : null}
                {transactionObj?.signature &&
                    <CustomInfoCard
                        title="Signature"
                        color="violet"
                        icon={
                            <IconSchool size={16} />
                        }
                        list={[
                            {
                                label: '',
                                value: transactionObj.signature && <Image
                                    fit="contain"
                                    w="auto"
                                    mah={200}
                                    maw="100%"
                                    src={`data:image/png;base64,${transactionObj.signature}`}
                                />
                            },
                        ]}
                    />
                }
                {transactionObj?.paymentSlipRequired && <>
                    <TransactionPaymentCard transaction={transactionObj} />
                </>}
            </SimpleGrid >

            <Space h="xl" />
            <Stack gap="md">
                <Grid gap="lg">
                    {/* show approval */}
                    <Grid.Col span={{ base: 12, md: 6 }}>
                        <TransactionsActions transactionObj={transactionObj} transactionRef={transactionRef} />
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 6 }}>
                        {/* Show product comments */}
                        <CustomProductComment
                            collectionRef={transactionRef}
                        />
                    </Grid.Col>
                </Grid>
                <Box mt="md" pt="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }} />
            </Stack>
        </>
    );
};

export default TransactionInfo;
